/**
 * Drug Program Detail Page
 * 
 * Comprehensive view of individual drug program with phased schedules and pharmacy data
 */

import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { drugProgramAPI, apiHelpers, getProductImageUrl } from '../services/api'
import { useAuthStore } from '../stores/authStore'
import { canSeeDrugPrices } from '../lib/drugPricing'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Button,
  Badge,
  Separator
} from '../components/ui'
import DrugProgramEditModal from '../components/DrugProgramEditModal'
import {
  ComposableMap,
  Geographies,
  Geography
} from 'react-simple-maps'

const geoUrl = "/gz_2010_us_040_00_20m.json"

export default function DrugProgramDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, currentClinicId } = useAuthStore()
  const showPrices = canSeeDrugPrices(user)
  
  const [program, setProgram] = useState(null)
  const [pharmacies, setPharmacies] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [hoveredState, setHoveredState] = useState(null)

  // Check if user has access
  const hasClinicAccess = user?.role === 'super_admin' || (currentClinicId && user?.clinicIds?.includes(currentClinicId))

  useEffect(() => {
    if (hasClinicAccess) {
      fetchProgramDetails()
      fetchProgramPharmacies()
    }
  }, [id, hasClinicAccess])

  const fetchProgramDetails = async () => {
    try {
      const response = await drugProgramAPI.get(id)
      if (response.data.success) {
        setProgram(response.data.data)
      }
    } catch (err) {
      console.error('Failed to fetch program details:', err)
      setError(apiHelpers.handleError(err).message)
    }
  }

  const fetchProgramPharmacies = async () => {
    try {
      const response = await drugProgramAPI.getPharmacies(id)
      if (response.data.success) {
        setPharmacies(response.data.data.pharmacies || [])
      }
    } catch (err) {
      console.error('Failed to fetch program pharmacies:', err)
    } finally {
      setLoading(false)
    }
  }

  const formatPrice = (price) => {
    if (!price) return 'N/A'
    return `$${(price / 100).toFixed(2)}`
  }

  const formatFillSchedule = (schedule) => {
    if (!schedule || !schedule.fill || schedule.fill.length === 0) return null

    return schedule.fill.map((fill, index) => ({
      step: index + 1,
      days: fill.days_since_last_fill,
      drug: fill.drug,
      entity: fill.program_entity
    }))
  }

  // Get unique states from all pharmacies offering this program
  const getAvailableStates = () => {
    const allStates = new Set()
    pharmacies.forEach(pharmacy => {
      if (pharmacy.states && Array.isArray(pharmacy.states)) {
        pharmacy.states.forEach(state => allStates.add(state.toUpperCase()))
      }
    })
    return Array.from(allStates).sort()
  }

  // State abbreviation lookup
  const getStateAbbreviation = (stateName) => {
    const stateMap = {
      'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
      'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
      'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
      'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
      'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
      'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
      'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
      'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
      'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
      'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
    }
    return stateMap[stateName] || stateName
  }

  // Aggregate state coverage across all pharmacies offering this program
  // Best coverage wins: sterile > non_sterile > legacy; controlled is additive
  const getStateCoverage = (stateCode) => {
    let hasSterile = false
    let hasNonSterile = false
    let hasControlled = false
    let hasLegacy = false

    pharmacies.forEach(ph => {
      if (ph.states_sterile?.includes(stateCode)) hasSterile = true
      if (ph.states_non_sterile?.includes(stateCode)) hasNonSterile = true
      if (ph.states_controlled?.includes(stateCode)) hasControlled = true
      if (ph.states?.includes(stateCode)) hasLegacy = true
    })

    let baseType = 'none'
    if (hasSterile) baseType = 'sterile'
    else if (hasNonSterile) baseType = 'non_sterile'
    else if (hasLegacy) baseType = 'legacy'

    return { baseType, hasSterile, hasNonSterile, hasControlled, hasLegacy, isLicensed: baseType !== 'none' || hasControlled }
  }

  const getMapStateFill = (stateCode) => {
    const info = getStateCoverage(stateCode)
    if (info.hasControlled) {
      if (info.hasSterile) return 'url(#dp-stripes-green)'
      if (info.hasNonSterile) return 'url(#dp-stripes-orange)'
      return 'url(#dp-stripes-gray)'
    }
    switch (info.baseType) {
      case 'sterile': return '#16a34a'
      case 'non_sterile': return '#ea580c'
      case 'legacy': return '#6b7280'
      default: return '#e5e7eb'
    }
  }

  const getCoverageLabel = (stateCode) => {
    const info = getStateCoverage(stateCode)
    const parts = []
    if (info.hasControlled) parts.push('Controlled (DEA)')
    if (info.hasSterile) parts.push('Sterile')
    else if (info.hasNonSterile) parts.push('Non-Sterile')
    else if (info.hasLegacy) parts.push('Legacy')
    if (parts.length === 0) return 'Not Available'
    return parts.join(' + ')
  }

  // Modal handlers
  const handleProgramSave = (updatedProgram) => {
    console.log('Drug program saved:', updatedProgram)
    setProgram(updatedProgram)
    // TODO: Update backend and refresh data
  }

  if (!hasClinicAccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Clinic Selection Required</CardTitle>
            <CardDescription>Please select a clinic from the header dropdown to view program details.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="loading-spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading program details...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error Loading Program</CardTitle>
            <CardDescription>{error}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate('/drug-programs')}>
                Back to Programs
              </Button>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Program Not Found</CardTitle>
            <CardDescription>The requested drug program could not be found.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" onClick={() => navigate('/drug-programs')}>
              Back to Programs
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  const fillSchedule = formatFillSchedule(program.fill_schedule)
  const isPhased = fillSchedule && fillSchedule.length > 1
  
  // Calculate average INGREDIENT price (drugs[0].current_price - the important one for routing)
  const pharmaciesWithIngredientPrice = program.pharmacies?.filter(p => 
    p.drugs && p.drugs[0] && p.drugs[0].current_price > 0
  ) || []
  const avgIngredientPrice = pharmaciesWithIngredientPrice.length > 0
    ? pharmaciesWithIngredientPrice.reduce((sum, p) => sum + p.drugs[0].current_price, 0) / pharmaciesWithIngredientPrice.length
    : 0
  
  // Also track average shipping price (less important)
  const avgShippingPrice = program.pharmacies?.length > 0 ? 
    program.pharmacies.reduce((sum, p) => sum + (p.current_price || 0), 0) / program.pharmacies.length : 0

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b">
        <div className="container mx-auto px-4 py-4 sm:py-6">
          {/* Back button - separate row on mobile */}
          <Button 
            variant="outline" 
            onClick={() => navigate('/drug-programs')}
            className="flex items-center gap-2 mb-3 sm:mb-4"
            size="sm"
          >
            ← Back
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0 flex-1 flex items-start gap-4">
              {getProductImageUrl(program.dosage?.img) && (
                <img
                  src={getProductImageUrl(program.dosage?.img)}
                  alt={program.drug_name}
                  className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-contain bg-muted flex-shrink-0"
                  onError={(e) => { e.target.style.display = 'none' }}
                />
              )}
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-foreground break-words">{program.drug_name}</h1>
                <p className="text-sm text-muted-foreground mt-1 break-all">
                  {program.entity_id} • {program.dosespot_type}
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {program.dea && <Badge variant="destructive">DEA</Badge>}
              {program.local_pharmacy && <Badge variant="secondary">Local</Badge>}
              {isPhased && <Badge variant="outline">Phased</Badge>}
              <Button 
                variant="outline" 
                onClick={() => setIsEditModalOpen(true)}
                size="sm"
              >
                Edit
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Available Pharmacies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{program.pharmacies?.length || 0}</div>
              <p className="text-xs text-muted-foreground">
                Offering this program
              </p>
            </CardContent>
          </Card>

          {/* Drug costs are what the pharmacy charges us, and are withheld
              from the clinic-facing roles. The API strips them too — see
              backend/src/utils/drugPricing.js. */}
          {showPrices && (
            <Card className="border-primary/30 bg-primary/5">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ingredient Price</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary">{formatPrice(avgIngredientPrice)}</div>
                <p className="text-xs text-muted-foreground">
                  Avg across {pharmaciesWithIngredientPrice.length} pharmacies
                </p>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Program Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold capitalize">{program.dosespot_type}</div>
              <p className="text-xs text-muted-foreground">
                {isPhased ? `${fillSchedule.length} phases` : 'Standard dosing'}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">State Availability</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{getAvailableStates().length}</div>
              <p className="text-xs text-muted-foreground">
                {getAvailableStates().length > 0 
                  ? getAvailableStates().slice(0, 5).join(', ') + (getAvailableStates().length > 5 ? '...' : '')
                  : 'No states available'}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Program Information */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Program Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Basic Information */}
                <div>
                  <h4 className="font-medium mb-2">Basic Information</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><span className="font-medium">Entity ID:</span> {program.entity_id}</p>
                    <p><span className="font-medium">Type:</span> {program.dosespot_type}</p>
                    {program.type && <p><span className="font-medium">Category:</span> {program.type}</p>}
                    {program.marketing_name && <p><span className="font-medium">Marketing Name:</span> {program.marketing_name}</p>}
                  </div>
                </div>

                <Separator />

                {/* Requirements */}
                <div>
                  <h4 className="font-medium mb-2">Requirements</h4>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><span className="font-medium">DEA Required:</span> {program.dea ? 'Yes' : 'No'}</p>
                    <p><span className="font-medium">Local Pharmacy Only:</span> {program.local_pharmacy ? 'Yes' : 'No'}</p>
                    <p><span className="font-medium">Sync Required:</span> {program.sync_required ? 'Yes' : 'No'}</p>
                    <p><span className="font-medium">Can Create Order:</span> {program.can_create_order !== false ? 'Yes' : 'No'}</p>
                  </div>
                </div>

                <Separator />

                {/* Dosage Information */}
                {program.dosage && (
                  <>
                    <div>
                      <h4 className="font-medium mb-2">Dosage Information</h4>
                      <div className="text-sm text-muted-foreground space-y-1">
                        {program.dosage.dosage && <p><span className="font-medium">Dosage:</span> {program.dosage.dosage}</p>}
                        {program.dosage.type && <p><span className="font-medium">Type:</span> {program.dosage.type}</p>}
                        {program.dosage.period && <p><span className="font-medium">Period:</span> {program.dosage.period}</p>}
                        {program.dosage.weeks_supply && <p><span className="font-medium">Weeks Supply:</span> {program.dosage.weeks_supply}</p>}
                        {program.dosage.description && (
                          <div>
                            <span className="font-medium">Description:</span>
                            <p className="mt-1 text-xs">{program.dosage.description}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <Separator />
                  </>
                )}

                {/* Tags */}
                {program.tags && program.tags.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-2">Tags</h4>
                    <div className="flex flex-wrap gap-1">
                      {program.tags.map((tag, index) => (
                        <Badge key={index} variant="outline" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

              </CardContent>
            </Card>

            {/* State Coverage Map */}
            {pharmacies.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>State Coverage</CardTitle>
                  <CardDescription>
                    {getAvailableStates().length} states across {pharmacies.length} pharmacies
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Legend */}
                  <div className="flex flex-wrap gap-3 text-xs">
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-3 rounded" style={{ backgroundColor: '#16a34a' }}></div>
                      <span>Sterile</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-3 rounded" style={{ backgroundColor: '#ea580c' }}></div>
                      <span>Non-Sterile</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <svg width="16" height="12" className="rounded overflow-hidden">
                        <defs>
                          <pattern id="legend-dp-stripes" patternUnits="userSpaceOnUse" width="4" height="4" patternTransform="rotate(45)">
                            <rect width="4" height="4" fill="#16a34a" />
                            <line x1="0" y1="0" x2="0" y2="4" stroke="#064e3b" strokeWidth="2" />
                          </pattern>
                        </defs>
                        <rect width="16" height="12" fill="url(#legend-dp-stripes)" />
                      </svg>
                      <span>+ DEA (stripes)</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-4 h-3 rounded" style={{ backgroundColor: '#e5e7eb' }}></div>
                      <span>Not Available</span>
                    </div>
                  </div>

                  {/* US State Map */}
                  <div className="relative border rounded-lg p-4 bg-slate-50 dark:bg-slate-900/50">
                    <ComposableMap
                      projection="geoAlbersUsa"
                      className="w-full h-48"
                      width={800}
                      height={400}
                    >
                      <defs>
                        <pattern id="dp-stripes-green" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                          <rect width="8" height="8" fill="#16a34a" />
                          <line x1="0" y1="0" x2="0" y2="8" stroke="#064e3b" strokeWidth="3" />
                        </pattern>
                        <pattern id="dp-stripes-orange" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                          <rect width="8" height="8" fill="#ea580c" />
                          <line x1="0" y1="0" x2="0" y2="8" stroke="#7c2d12" strokeWidth="3" />
                        </pattern>
                        <pattern id="dp-stripes-gray" patternUnits="userSpaceOnUse" width="8" height="8" patternTransform="rotate(45)">
                          <rect width="8" height="8" fill="#9ca3af" />
                          <line x1="0" y1="0" x2="0" y2="8" stroke="#374151" strokeWidth="3" />
                        </pattern>
                      </defs>
                      <Geographies geography={geoUrl}>
                        {({ geographies }) =>
                          geographies.map((geo) => {
                            const stateName = geo.properties.NAME
                            const stateCode = getStateAbbreviation(stateName)
                            const info = getStateCoverage(stateCode)
                            const fill = getMapStateFill(stateCode)

                            return (
                              <Geography
                                key={geo.rsmKey}
                                geography={geo}
                                fill={fill}
                                stroke="#374151"
                                strokeWidth={0.5}
                                onMouseEnter={() => {
                                  setHoveredState({
                                    name: stateName,
                                    code: stateCode,
                                    label: getCoverageLabel(stateCode)
                                  })
                                }}
                                onMouseLeave={() => setHoveredState(null)}
                                style={{
                                  default: { outline: "none" },
                                  hover: {
                                    fill: info.isLicensed ? fill : '#94a3b8',
                                    stroke: "#1f2937",
                                    strokeWidth: 2,
                                    outline: "none",
                                  },
                                  pressed: { fill, stroke: "#374151", strokeWidth: 2, outline: "none" },
                                }}
                              />
                            )
                          })
                        }
                      </Geographies>
                    </ComposableMap>

                    {hoveredState && (
                      <div className="absolute top-2 left-2 bg-black text-white px-2 py-1 rounded text-xs font-medium z-10">
                        {hoveredState.name} ({hoveredState.code}) - {hoveredState.label}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Phased Schedule */}
            {isPhased && (
              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Phased Schedule</CardTitle>
                  <CardDescription>
                    This program follows a {fillSchedule.length}-step titration schedule
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {fillSchedule.map((phase, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 border rounded-lg">
                        <div className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-medium">
                          {phase.step}
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-sm">
                            {phase.days === 0 ? 'Start' : `Day ${phase.days}`}
                          </div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {phase.drug}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Available Pharmacies */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Available Pharmacies ({pharmacies.length})</CardTitle>
                <CardDescription>
                  Pharmacies offering this program with current pricing
                </CardDescription>
              </CardHeader>
              <CardContent>
                {pharmacies.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p>No pharmacies found offering this program.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pharmacies.map((pharmacy, index) => (
                      <Card key={index} className="border-l-4 border-l-primary">
                        <CardHeader className="pb-3">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base sm:text-lg">
                                <Link 
                                  to={`/pharmacy/${pharmacy.pharmacy_ncpdpid || 'unknown'}`}
                                  className="hover:underline text-primary break-words"
                                >
                                  {pharmacy.pharmacy_name}
                                </Link>
                              </CardTitle>
                              <CardDescription className="mt-1">
                                {pharmacy.pharmacy_ncpdpid && `NCPDP: ${pharmacy.pharmacy_ncpdpid}`}
                              </CardDescription>
                              {/* States served by this pharmacy */}
                              {pharmacy.states && pharmacy.states.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                  {pharmacy.states.slice(0, 6).map((state, stateIndex) => (
                                    <Badge key={stateIndex} variant="outline" className="text-xs">
                                      {state}
                                    </Badge>
                                  ))}
                                  {pharmacy.states.length > 6 && (
                                    <Badge variant="outline" className="text-xs">
                                      +{pharmacy.states.length - 6}
                                    </Badge>
                                  )}
                                </div>
                              )}
                            </div>
                            {showPrices && (
                              <div className="text-left sm:text-right">
                                <div className="text-lg font-bold text-primary">
                                  {formatPrice(pharmacy.program_specific?.drugs?.[0]?.current_price)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Ingredient price
                                </div>
                              </div>
                            )}
                          </div>
                        </CardHeader>
                        
                        {pharmacy.program_specific?.drugs && pharmacy.program_specific.drugs.length > 0 && (
                          <CardContent className="pt-0">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h5 className="font-medium text-sm">Drug Configurations ({pharmacy.program_specific.drugs.length})</h5>
                                {showPrices && (pharmacy.program_specific?.current_price || 0) > 0 && (
                                  <span className="text-xs text-muted-foreground">
                                    + {formatPrice(pharmacy.program_specific.current_price)} shipping
                                  </span>
                                )}
                              </div>
                              {pharmacy.program_specific.drugs.slice(0, 3).map((drug, drugIndex) => (
                                <div key={drugIndex} className="bg-muted p-3 rounded-lg">
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 text-sm">
                                    <div className="col-span-1 sm:col-span-2">
                                      <span className="text-muted-foreground text-xs">Drug:</span>
                                      <div className="font-medium text-sm">{drug.drug_name}</div>
                                    </div>
                                    {showPrices && (
                                      <div className="flex justify-between sm:block">
                                        <span className="text-muted-foreground text-xs">Ingredient Price:</span>
                                        <span className="font-semibold text-primary sm:block">{formatPrice(drug.current_price)}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between sm:block">
                                      <span className="text-muted-foreground text-xs">Quantity:</span>
                                      <span className="font-medium sm:block">{drug.dispense_quantity} {drug.dispense_unit}</span>
                                    </div>
                                    <div className="flex justify-between sm:block col-span-1 sm:col-span-2">
                                      <span className="text-muted-foreground text-xs">Days:</span>
                                      <span className="font-medium sm:block">{drug.days} days</span>
                                    </div>
                                  </div>
                                  {drug.sig && (
                                    <div className="mt-2 pt-2 border-t border-border/50">
                                      <span className="text-muted-foreground text-xs">Instructions:</span>
                                      <div className="text-xs mt-1 text-muted-foreground break-words">
                                        {drug.sig.length > 80 ? `${drug.sig.substring(0, 80)}...` : drug.sig}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                              {pharmacy.program_specific.drugs.length > 3 && (
                                <div className="text-center">
                                  <Badge variant="outline" className="text-xs">
                                    +{pharmacy.program_specific.drugs.length - 3} more configurations
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Edit Drug Program Modal */}
      <DrugProgramEditModal
        program={program}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleProgramSave}
      />
    </div>
  )
}
